import { type MessageKey, t } from '../i18n';
import { api, ApiError } from '../net/api';
import type { Limits } from '../net/protocol';
import { formatMoney } from '../util/format';
import { AVATAR_COUNT, avatarSvg } from './avatars';
import type { Menu } from './menu';

/**
 * The two read-and-set pages in the drawer: the avatar chooser and the operator
 * limits.
 *
 * They share a file because they share a shape -- a page built from session data,
 * redrawn when the language changes -- and because neither is big enough to earn
 * a module of its own.
 */

export interface AvatarPanelHost {
  /** The avatar the server currently holds for this player. */
  current: () => number;
  /** Called after the server has accepted a new one. */
  onChosen: (avatarId: number) => void;
  /** Somewhere to say it did not work. */
  notify: (message: string, kind?: 'win' | 'bad') => void;
}

export class AvatarPanel {
  constructor(
    private readonly menu: Menu,
    private readonly host: AvatarPanelHost,
  ) {}

  show(): void {
    this.menu.showPage('menu.avatar', (page) => this.build(page));
  }

  private build(page: HTMLElement): void {
    const hint = document.createElement('p');
    hint.textContent = t('avatar.hint');

    const grid = document.createElement('div');
    grid.className = 'avatar-grid';

    const buttons: HTMLButtonElement[] = [];
    const mark = (chosen: number): void => {
      buttons.forEach((b, id) => b.setAttribute('aria-pressed', id === chosen ? 'true' : 'false'));
    };

    for (let id = 0; id < AVATAR_COUNT; id += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      // "Avatar 7" rather than twenty names to translate into twenty-three
      // languages. A screen reader needs to tell the options apart and to say
      // which one is selected, and aria-pressed carries the second part; a
      // vocabulary of animal names would carry no more information than the
      // number does.
      button.setAttribute('aria-label', t('avatar.option', { n: id + 1 }));
      button.appendChild(avatarSvg(id));
      button.addEventListener('click', () => {
        // No optimistic switch here, unlike the audio toggles: the avatar is how
        // other players see you, and "it looked saved but was not" is a support
        // ticket. The tick moves when the server says so.
        void api
          .setAvatar(id)
          .then(() => {
            mark(id);
            this.host.onChosen(id);
            this.host.notify(t('avatar.saved'), 'win');
          })
          .catch((e: unknown) => {
            this.host.notify(
              e instanceof ApiError ? e.message : t('avatar.failed'),
              'bad',
            );
          });
      });
      buttons.push(button);
      grid.appendChild(button);
    }

    mark(this.host.current());
    page.replaceChildren(hint, grid);
  }
}

export interface LimitsPanelHost {
  /** The limits from the handshake, or null before it has answered. */
  limits: () => Limits | null;
  currency: () => string;
  scale: () => number;
  symbol: () => string;
}

/**
 * The operator's limits, read-only.
 *
 * Every number on this page comes from the handshake, which reads it from the
 * `currencies` row the server actually enforces (store/bets.go checks the stake
 * against min_bet/max_bet and caps a payout at max_payout). Nothing here is a
 * constant in the client: a screen that shows 1 000 while the server refuses at
 * 5 000 -- or pays out a capped win the player was never told about -- is worse
 * than no screen.
 *
 * This is deliberately not the responsible-gambling card. That one is the
 * player's own limits and is editable; this one is the house's and is not.
 */
export class LimitsPanel {
  constructor(
    private readonly menu: Menu,
    private readonly host: LimitsPanelHost,
  ) {}

  show(): void {
    this.menu.showPage('menu.gameLimits', (page) => this.build(page));
  }

  private build(page: HTMLElement): void {
    const limits = this.host.limits();
    if (!limits) {
      const p = document.createElement('p');
      p.textContent = t('limits.unavailable');
      page.replaceChildren(p);
      return;
    }

    const money = (value: string): string =>
      formatMoney(value, this.host.scale(), this.host.symbol());

    const box = document.createElement('div');
    box.className = 'menu-block';
    box.append(
      row('limits.minBet', money(limits.minBet)),
      row('limits.maxBet', money(limits.maxBet)),
      row('limits.maxWin', money(limits.maxPayout)),
    );

    const note = document.createElement('p');
    note.textContent = t('limits.note', { currency: this.host.currency() });

    const own = document.createElement('p');
    own.textContent = t('limits.yourOwn');

    page.replaceChildren(box, note, own);
  }
}

function row(labelKey: MessageKey, value: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'limit-row';

  const label = document.createElement('span');
  label.textContent = t(labelKey);

  const amount = document.createElement('span');
  amount.className = 'limit-value';
  amount.textContent = value;

  el.append(label, amount);
  return el;
}
